import { useEffect, useMemo, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { SlidersHorizontal, X, ChevronDown } from "lucide-react";
import { api } from "@/lib/api";
import V2ProductCard from "@/v2/ProductCard";
import { matchesFilters, SIZE_OPTIONS, STYLE_OPTIONS, ROOM_OPTIONS } from "@/v2/lib/deriveTags";

const DEFAULT_CAT_LABELS = {
  all: "Everything",
  carpets: "Rugs & Carpets",
  rugs: "Jute Rugs",
  "photo-frames": "Home Décor",
  "kids-baskets": "Bags & Baskets",
};

const DEFAULT_CAT_BANNERS = {
  all: { title: "The Iryaa Collection", subtitle: "Rugs, décor and small textures — handcrafted with intention." },
  carpets: { title: "Hand-Tufted Rugs", subtitle: "Wool, chenille and cotton — pieces that soften and anchor a room in equal measure." },
  rugs: { title: "Jute Rugs", subtitle: "Undyed, hand-braided, kind to bare feet and quiet living." },
  "photo-frames": { title: "Wall Décor & Wood", subtitle: "Rustic frames and small wooden pieces, hand-finished by our carpenter cousins." },
  "kids-baskets": { title: "Bags & Baskets", subtitle: "Cotton, jute and rope — kind, sturdy, considered." },
};

const FILTER_KEYS = ["cat", "material", "style", "size", "room", "filter"];

// Labels used in the "active filter" pills
const PILL_LABEL = {
  cat: "Category",
  material: "Material",
  style: "Style",
  size: "Size",
  room: "Room",
  filter: "Curated",
};

function titleCase(s) {
  return s.replace(/\b\w/g, (c) => c.toUpperCase());
}

export default function V2Collections() {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [params, setParams] = useSearchParams();
  const [sort, setSort] = useState("featured");
  const [filterOpen, setFilterOpen] = useState(false);

  const cat = params.get("cat") || "all";

  useEffect(() => {
    api.get("/products").then((r) => setProducts(r.data.products || []));
    const load = () => api.get("/categories").then((r) => setCategories(r.data.categories || [])).catch(() => {});
    load();
    window.addEventListener("iryaa:categories-changed", load);
    return () => window.removeEventListener("iryaa:categories-changed", load);
  }, []);

  // Merge the "all" pseudo-category with the admin-managed categories.
  const catLabels = useMemo(() => {
    const map = { all: "Everything" };
    for (const c of categories) map[c.slug] = c.label;
    // Fall back to defaults if API hasn't loaded yet
    if (categories.length === 0) return DEFAULT_CAT_LABELS;
    return map;
  }, [categories]);

  const banner = useMemo(() => {
    if (cat === "all") return DEFAULT_CAT_BANNERS.all;
    const c = categories.find((x) => x.slug === cat);
    if (c && (c.banner_title || c.banner_subtitle)) {
      return { title: c.banner_title || c.label, subtitle: c.banner_subtitle || "" };
    }
    return DEFAULT_CAT_BANNERS[cat] || { title: catLabels[cat] || "Collection", subtitle: "" };
  }, [cat, categories, catLabels]);

  // Read all filter params
  const activeFilters = useMemo(() => {
    const o = {};
    for (const k of FILTER_KEYS) {
      const v = params.get(k);
      if (v) o[k] = v;
    }
    return o;
  }, [params]);

  const filtered = useMemo(() => {
    let items = products.filter((p) => matchesFilters(p, activeFilters));

    // Curatorial "filter" param (new / bestsellers) — sort/limit
    if (activeFilters.filter === "new") {
      items = [...items].sort((a, b) => (b.order || 0) - (a.order || 0)).slice(0, 12);
    } else if (activeFilters.filter === "bestsellers") {
      items = [...items]
        .sort((a, b) => ((b.mrp || 0) - b.price_from) - ((a.mrp || 0) - a.price_from))
        .slice(0, 12);
    }

    if (sort === "price-asc") items.sort((a, b) => a.price_from - b.price_from);
    else if (sort === "price-desc") items.sort((a, b) => b.price_from - a.price_from);
    else if (sort === "discount") items.sort((a, b) => ((b.mrp || 0) - b.price_from) - ((a.mrp || 0) - a.price_from));
    return items;
  }, [products, activeFilters, sort]);

  const removeParam = (key) => {
    const next = new URLSearchParams(params);
    next.delete(key);
    setParams(next);
  };

  const clearAll = () => setParams({});

  const nonCatPills = Object.entries(activeFilters).filter(([k]) => k !== "cat");

  return (
    <div data-testid="v2-collections-page">
      {/* Banner */}
      <section className="max-w-[1440px] mx-auto px-6 md:px-12 pt-14 md:pt-20 pb-8">
        <div className="text-[11px] uppercase tracking-[0.24em] text-[#7a7466]">
          <Link to="/" className="v2-link">Home</Link> / <span className="text-[#1a1a1a]">{catLabels[cat] || "Collection"}</span>
        </div>
        <h1 className="mt-6 font-serif-warm text-5xl md:text-6xl lg:text-7xl leading-[1.02] tracking-tight max-w-4xl">
          {banner.title}
        </h1>
        <p className="mt-6 text-[16px] text-[#4a4640] leading-relaxed font-light max-w-2xl">{banner.subtitle}</p>
      </section>

      {/* Filter bar */}
      <section className="sticky top-20 z-30 bg-[#fbf9f5]/92 backdrop-blur-xl border-y border-[#e4ded2]">
        <div className="max-w-[1440px] mx-auto px-6 md:px-12 py-4 flex items-center justify-between gap-4">
          <div className="flex gap-1 overflow-x-auto no-scrollbar">
            {Object.entries(catLabels).map(([slug, label]) => (
              <button
                key={slug}
                data-testid={`v2-filter-${slug}`}
                onClick={() => setParams(slug === "all" ? {} : { cat: slug })}
                className={`px-4 py-2 text-[11px] uppercase tracking-[0.2em] whitespace-nowrap transition-colors ${
                  cat === slug ? "bg-[#1a1a1a] text-[#fbf9f5]" : "text-[#1a1a1a] hover:text-[#b85742]"
                }`}
              >
                {label}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-3">
            <span className="hidden md:inline text-[11px] uppercase tracking-[0.2em] text-[#7a7466]">{filtered.length} pieces</span>
            <div className="relative">
              <select
                value={sort}
                onChange={(e) => setSort(e.target.value)}
                data-testid="v2-sort"
                className="appearance-none border border-[#c9c0ae] bg-transparent pl-4 pr-9 py-2 text-[11px] uppercase tracking-[0.2em] outline-none focus:border-[#1a1a1a] cursor-pointer"
              >
                <option value="featured">Featured</option>
                <option value="price-asc">Price · Low to High</option>
                <option value="price-desc">Price · High to Low</option>
                <option value="discount">Best Discount</option>
              </select>
              <ChevronDown size={12} className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none opacity-60" />
            </div>
            <button onClick={() => setFilterOpen(true)} data-testid="v2-open-filters" className="lg:hidden inline-flex items-center gap-2 border border-[#c9c0ae] px-3 py-2 text-[11px] uppercase tracking-[0.2em]">
              <SlidersHorizontal size={13} /> Filter
            </button>
          </div>
        </div>

        {/* Active filter pills */}
        {nonCatPills.length > 0 && (
          <div className="max-w-[1440px] mx-auto px-6 md:px-12 pb-3 flex flex-wrap items-center gap-2" data-testid="v2-active-filters">
            <span className="text-[10px] uppercase tracking-[0.24em] text-[#7a7466] mr-1">Filters:</span>
            {nonCatPills.map(([k, v]) => (
              <button
                key={k}
                onClick={() => removeParam(k)}
                data-testid={`v2-pill-${k}`}
                className="inline-flex items-center gap-2 border border-[#c9c0ae] px-3 py-1 text-[11px] uppercase tracking-[0.18em] hover:border-[#1a1a1a] hover:text-[#b85742] transition-colors"
              >
                <span className="text-[#7a7466]">{PILL_LABEL[k] || k}:</span>
                <span className="text-[#1a1a1a]">{titleCase(v)}</span>
                <X size={11} />
              </button>
            ))}
            <button
              onClick={clearAll}
              data-testid="v2-clear-filters"
              className="ml-1 text-[11px] uppercase tracking-[0.18em] text-[#b85742] v2-link"
            >
              Clear all
            </button>
          </div>
        )}
      </section>

      {/* Products grid */}
      <section className="max-w-[1440px] mx-auto px-6 md:px-12 py-14">
        {filtered.length === 0 ? (
          <div className="py-24 text-center">
            <div className="font-serif-warm text-3xl text-[#1a1a1a]">Nothing here yet.</div>
            <p className="mt-4 text-[#4a4640] font-light max-w-md mx-auto">
              {nonCatPills.length > 0
                ? "No pieces match this exact combination — try clearing a filter, or commission a piece from our Custom Studio."
                : "This collection is being woven. Please check back soon, or commission a piece from our Custom Studio."}
            </p>
            <div className="mt-8 flex gap-3 justify-center">
              {nonCatPills.length > 0 && (
                <button onClick={clearAll} className="v2-btn-outline">Clear filters</button>
              )}
              <Link to="/custom" className="v2-btn-primary">Commission a piece</Link>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 md:gap-8">
            {filtered.map((p) => <V2ProductCard key={p.id} product={p} testIdPrefix="v2-collections" />)}
          </div>
        )}
      </section>

      {/* Mobile filter drawer */}
      {filterOpen && (
        <div className="fixed inset-0 z-50 lg:hidden" data-testid="v2-filter-drawer">
          <div className="absolute inset-0 bg-[#1a1a1a]/50" onClick={() => setFilterOpen(false)} />
          <aside className="absolute bottom-0 left-0 right-0 bg-[#fbf9f5] max-h-[80vh] overflow-y-auto">
            <div className="flex justify-between items-center px-6 py-5 border-b border-[#e4ded2]">
              <div className="overline-v2">Filter & Sort</div>
              <button onClick={() => setFilterOpen(false)}><X size={20} /></button>
            </div>
            <div className="p-6 space-y-6">
              <div>
                <div className="text-[13px] font-medium mb-3">Category</div>
                <div className="flex flex-wrap gap-2">
                  {Object.entries(catLabels).map(([slug, label]) => (
                    <button
                      key={slug}
                      onClick={() => { setParams(slug === "all" ? {} : { cat: slug }); setFilterOpen(false); }}
                      className={`px-3 py-2 text-[11px] uppercase tracking-[0.18em] border ${
                        cat === slug ? "bg-[#1a1a1a] text-[#fbf9f5] border-[#1a1a1a]" : "border-[#c9c0ae]"
                      }`}
                    >{label}</button>
                  ))}
                </div>
              </div>
            </div>
          </aside>
        </div>
      )}
    </div>
  );
}
